import React, { type InputHTMLAttributes, useId } from 'react'
import styles from './inputText.module.scss'
import { Icon, type IconProps } from '../Icon/Icon'

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Props extends a DOM attributes interface; interface extension is idiomatic here
export interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  txtInfo?: React.ReactNode
  error?: boolean
  inputClassName?: string
  rightIcon?: IconProps
  withoutLeftBorder?: boolean
  disabledColor?: 'light-cream' | 'white'
}

export const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(({
  label,
  txtInfo,
  error,
  value,
  className,
  type = 'text',
  inputClassName = '',
  rightIcon,
  withoutLeftBorder,
  disabledColor = 'light-cream',
  ...props
}, ref) => {
  // The label names the field for a screen reader only while the two are
  // associated, so the input carries an id and the label points at it — the same
  // pattern as InputCheck, and an `id` from outside still wins.
  const genId = useId()
  const inputId = props.id ?? genId
  const active = String(value ?? '') !== '' || (label != null && props.placeholder != null)
  const classes = [
    className ?? '',
    styles['input-text'],
    'modul',
    'full-width',
    active ? styles['input-text--active'] : '',
    error ? styles['input-text--error'] : '',
    label == null ? styles['input-text--no-label'] : '',
  ].filter(Boolean).join(' ')

  const inputClasses = [
    inputClassName,
    'txt-p-1',
    'txt-p-1-s',
    withoutLeftBorder ? styles['input-text__input--flush-left'] : '',
    props.disabled ? `bg-${disabledColor}` : 'bg-white',
  ].filter(Boolean).join(' ')

  return (
    <div className={classes}>
      <div className="row-flx">
        {/* The label FOLLOWS the input, and that order is load-bearing: the
            floating-label styles select it as the adjacent sibling (`+ label`),
            so moving it silently breaks them and no gate command would see it. */}
        <input
          className={inputClasses}
          type={type}
          value={value}
          ref={ref}
          {...props}
          id={inputId}
        />
        {label != null && <label className="txt-p-1 txt-p-1-s" htmlFor={inputId}>{label}</label>}
      </div>
      {txtInfo != null && (
        <div className={`m-t-8 txt-bold-500 txt-light-kale ${styles['input-text__info']}`}>{txtInfo}</div>
      )}
      {rightIcon != null && (
        <div className={`cursor-pointer ${styles['input-text__right-icon']}`}>
          <Icon {...rightIcon} />
        </div>
      )}
    </div>
  )
})

InputText.displayName = 'InputText'
